import type { ReactNode } from 'react';
import { CalendarBlankIcon } from '@phosphor-icons/react';
import { Button } from '@voll-intelligence/ui-kit/ui/button';
import { parseDateOnly, toDateOnly } from '@voll-intelligence/dates';

/** Um chip da fileira: o dia (`YYYY-MM-DD`), o rótulo ("25 ago") e se ele
 *  pode ser escolhido. */
export type DateChipView = {
  readonly date: string;
  readonly label: string;
  readonly disabled: boolean;
};

/** O dia `days` dias depois (ou antes) — no calendário LOCAL. */
function shiftDay(date: string, days: number): string {
  const day = parseDateOnly(date);
  if (!day) return date;
  day.setDate(day.getDate() + days);
  return toDateOnly(day);
}

/**
 * A janela de dias em torno do dia buscado — D-2 … D+2 com `radius` 2 — e quais
 * NÃO podem ser escolhidos: antes do piso (`min`: hoje, ou a ida quando a
 * fileira é da volta) ou depois do teto (`max`: a volta, quando a fileira é da
 * ida). Pura: o host formata os rótulos.
 */
export function dateChipWindow(
  center: string,
  bounds: { readonly min?: string; readonly max?: string },
  radius = 2,
): { date: string; disabled: boolean }[] {
  return Array.from({ length: radius * 2 + 1 }, (_, index) => {
    const date = shiftDay(center, index - radius);
    const disabled =
      (bounds.min !== undefined && date < bounds.min) ||
      (bounds.max !== undefined && date > bounds.max);
    return { date, disabled };
  });
}

/**
 * A fileira de chips de data acima da lista (#2440): troca o dia SEM refazer o
 * planner. O dia em cena é o chip cheio; os outros são contorno. Um grupo de
 * botões com `aria-pressed`, não abas: trocar o dia dispara uma busca nova.
 */
export function ResultsDateChips({
  days,
  selected,
  label,
  disabled = false,
  onSelect,
}: {
  readonly days: readonly DateChipView[];
  readonly selected: string;
  /** O nome acessível do grupo ("Trocar a data"). */
  readonly label: string;
  /** Uma busca em voo: a fileira inteira trava. */
  readonly disabled?: boolean;
  readonly onSelect: (date: string) => void;
}): ReactNode {
  // No celular os cinco não cabem com o rótulo inteiro ("4 nov"): a fileira
  // rola de lado, como a dos chips de filtro, em vez de cortar o dia em "4 …"
  // (#6192). Cada chip parte da largura do desenho e, no largo, cresce até a
  // faixa inteira, em partes iguais. O chip pinta os 28px do desenho, mas o
  // toque pega 44px (o `after`); o `py-2 -my-2` dá à rolagem a folga para não
  // recortar esse alvo, sem mudar a altura da fileira.
  return (
    <div
      role="group"
      aria-label={label}
      className="-my-2 flex gap-2 overflow-x-auto py-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
    >
      {days.map((day) => {
        const current = day.date === selected;
        return (
          <Button
            key={day.date}
            type="button"
            size="sm"
            variant={current ? 'default' : 'outline'}
            aria-pressed={current}
            disabled={disabled || day.disabled}
            className="relative flex-[1_0_101px] rounded-full after:absolute after:inset-x-0 after:top-1/2 after:h-11 after:-translate-y-1/2"
            onClick={() => {
              if (!current) onSelect(day.date);
            }}
          >
            <CalendarBlankIcon aria-hidden="true" />
            <span className="whitespace-nowrap">{day.label}</span>
          </Button>
        );
      })}
    </div>
  );
}
